import { QuestionGroupType, QuestionStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { QuestionDuplicationModal } from '@exam-management/qcontent-duplication';
import { useNavigate } from '@exam-management/router';
import {
  ContentCopyRoundedIcon,
  DataUsageOutlinedIcon,
  EditRoundedIcon,
  HistoryIcon,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
  OpenInNewIcon,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useLogger } from '@shared/logger';
import { usePermission } from '@shared/permissions';

interface QContentListRowMoreActionMenuProps {
  category: QuestionGroupType;
  status: QuestionStatus;
  qId: string;
  qContentId: string;
  groupId: string;
  shouldRedirectTo: 'question' | 'questionGroup';
}

export default function QContentListRowMoreActionMenu(
  props: Readonly<QContentListRowMoreActionMenuProps>,
) {
  const { category, status, qId, qContentId, groupId, shouldRedirectTo = 'question' } = props;
  const logger = useLogger();
  const { permissions } = usePermission();
  const [isDuplicateModalOpen, duplicateModalHandlers] = useDisclosure(false);
  const { open: openDuplicateModal, close: closeDuplicateModal } = duplicateModalHandlers;
  const { qBankStructureInformation } = useQBankStructure();
  const navigate = useNavigate();

  const handleClickOpenNewTab = () => {
    if (category === QuestionGroupType.ItemSet) {
      if (shouldRedirectTo === 'question') {
        return window.open(
          `/qbank-structures/${qBankStructureInformation.id}/qcontents/item-set/${groupId}/${qContentId}`,
          '_blank',
        );
      } else if (shouldRedirectTo === 'questionGroup') {
        return window.open(
          `/qbank-structures/${qBankStructureInformation.id}/qcontents/item-set/${groupId}`,
          '_blank',
        );
      }
    }

    return window.open(
      `/qbank-structures/${qBankStructureInformation.id}/qcontents/single/${qContentId}`,
      '_blank',
    );
  };

  const handleClickEditMenu = () => {
    if (category === QuestionGroupType.ItemSet) {
      if (shouldRedirectTo === 'question') {
        return navigate(
          '/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId',
          {
            params: {
              questionId: groupId,
              structureId: qBankStructureInformation.id,
              subQuestionId: qContentId,
            },
          },
        );
      } else if (shouldRedirectTo === 'questionGroup') {
        return navigate('/qbank-structures/:structureId/qcontents/item-set/:questionId', {
          params: { questionId: groupId, structureId: qBankStructureInformation.id },
        });
      }
    }

    return navigate('/qbank-structures/:structureId/qcontents/single/:contentId', {
      params: { structureId: qBankStructureInformation.id, contentId: qContentId },
    });
  };

  const handleClickDuplicateMenu = () => {
    openDuplicateModal();
  };

  const handleClickQuestionUsageMenu = () => {
    logger.debug('View Question Usage QContent option');
  };

  const handleClickActivityLogMenu = () => {
    navigate('/qbank-structures/:structureId/qcontents/single/:contentId/activity-log', {
      params: {
        structureId: qBankStructureInformation.id,
        contentId: qContentId,
      },
    });
    logger.debug('View Activity Log QContent option');
  };

  const editOption: MoreMenuIconButtonOption = {
    icon: <EditRoundedIcon />,
    label: 'Edit',
    onClick: handleClickEditMenu,
  };

  const openNewTabOption: MoreMenuIconButtonOption = {
    icon: <OpenInNewIcon />,
    label: 'Open in New Tab',
    onClick: handleClickOpenNewTab,
  };

  const duplicateOption: MoreMenuIconButtonOption = {
    icon: <ContentCopyRoundedIcon />,
    label: 'Duplicate to',
    onClick: handleClickDuplicateMenu,
  };

  const viewUsageOption: MoreMenuIconButtonOption = {
    icon: <DataUsageOutlinedIcon />,
    label: 'Question Usage',
    onClick: handleClickQuestionUsageMenu,
  };

  const viewHistoryOption: MoreMenuIconButtonOption = {
    icon: <HistoryIcon />,
    label: 'Activity Log',
    onClick: handleClickActivityLogMenu,
  };

  const getOptions = () => {
    const allowedOptions: MoreMenuIconButtonOption[] = [];

    if (
      (status === QuestionStatus.Draft ||
        (status === QuestionStatus.Unpublished && category !== QuestionGroupType.ItemSet)) &&
      permissions.includes(APP_PERMISSIONS.QBANK.QUESTION.UPDATE)
    ) {
      allowedOptions.push(editOption);
    }
    if (
      category !== QuestionGroupType.ItemSet &&
      permissions.includes(APP_PERMISSIONS.QBANK.QUESTION.CREATE)
    ) {
      allowedOptions.push(duplicateOption);
    }
    if (status !== QuestionStatus.Draft) {
      allowedOptions.push(viewUsageOption, viewHistoryOption);
    }

    allowedOptions.push(openNewTabOption);

    return allowedOptions;
  };

  const menus = getOptions();

  return (
    <>
      <MoreMenuIconButton options={menus} />
      <QuestionDuplicationModal
        open={isDuplicateModalOpen}
        onClose={closeDuplicateModal}
        qId={qId}
        qContentId={qContentId}
      />
    </>
  );
}
